import type { Simplify, UnionToIntersection, UnknownArray } from 'type-fest'
import type { NonRecursiveType } from 'type-fest/source/internal'
import type { InternalPickDeep } from 'type-fest/source/pick-deep'

export type GroupProjection<K, V> = {
  id: K
  value: V
}

export type Undefined<T> = T | undefined

export type Arrayable<T> = T | T[]

export interface PaginationResponse<T> {
  recordsTotal: number
  recordsFiltered: number
  draw: number
  data: T[]
}
/**
 * type guard for keys of object iterators
 */
export function isKey<T extends object>(
  x: T,
  k: PropertyKey
): k is keyof T {
  return k in x
}
/**
 * Generates all possible **dot-separated property paths** for a given type `T`.
 * Derived from {@link https://stackoverflow.com/questions/77841033/creating-recursive-type-for-union-of-keys-in-nested-objects}
 *
 * - Supports nested objects
 * - Supports numeric indices for arrays (`posts.0.title`)
 * - Excludes functions
 *
 * @example
 * type Example = {
 *   id: string;
 *   user: {
 *     name: string;
 *     posts: { title: string; tags: string[] }[];
 *   };
 * };
 *
 * type Paths = ExtendedPropertyNames<Example>;
 * // "id" | "user" | "user.name"
 * // | "user.posts" | "user.posts.0" | "user.posts.0.title"
 * // | "user.posts.0.tags" | "user.posts.0.tags.0"
 */
export type ExtendedPropertyNames<T>
  = T extends object
    ? {
        [K in (string | number) & keyof T]:
        // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
        T[K] extends Function
          ? never
          : `${K}` | `${K & (string | number)}.${ExtendedPropertyNames<T[K]>}`
      }[(string | number) & keyof T]
    : T extends Record<string, unknown> // additional expansion for records, it will allow any key type as we don'y know what kind of values exists
      ? {
          [ K in (string) & keyof T]:
          // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
          T[K] extends Function
            ? never
            : `${K}` | `${K & string}.${ExtendedPropertyNames<T[K]>}`
        }[string & keyof T]
      : never
/**
 * Similar to PickDeep from type-fest but with our ExtendedPropertyNames
 */
export type ExtendedPickDeep<T, PathUnion extends string>
  = T extends NonRecursiveType
    ? never
    : T extends UnknownArray
      ? UnionToIntersection<{
        [P in PathUnion]: InternalPickDeep<T, P>;
      }[PathUnion]
      >
      : T extends object
        ? Simplify<UnionToIntersection<{
          [P in PathUnion]: InternalPickDeep<T, P>;
        }[PathUnion]>>
        : never

/**
 * Resolves the type of a property at the given path `P` within type `T`.
 *
 * - Matches the property path strings from `ExtendedPropertyNames<T>`
 * - Supports nested object and array access
 *
 * @example
 * type Example = { user: { posts: { title: string }[] } };
 *
 * type A = ExtendedPropertyTypes<Example, "user.posts.0.title">; // string
 */
export type ExtendedPropertyTypes<T, P extends string>
  = P extends keyof T & string // single level path
    ? T[P]
    : P extends `${infer K}.${infer Rest}` // nested path
      ? K extends keyof T & string // do we have a simple path?
        ? T[K] extends (infer U)[] // is this path an array?
          ? Rest extends `${number}.${infer SubRest}` // is the rest a nested path from the array?
            ? ExtendedPropertyTypes<U, SubRest> // recursively run this again with the array type as T and the array subpath
            : never
          : ExtendedPropertyTypes<T[K], Rest> // recursively run this again with the object type as T and the object subpath
        : never
      : never

/**
 * Combines `ExtendedPropertyNames<T>` and `ExtendedPropertyTypes<T, P>`:
 *
 * - Ensures `P` is a valid property path of `T`
 * - Resolves the correct type at that path
 *
 * @example
 * type Example = { user: { name: string } };
 *
 * type A = PathValue<Example, "user.name">; // string
 * type B = PathValue<Example, "invalid.path">; //  Error
 */
export type PathValue<T, P extends ExtendedPropertyNames<T>> = ExtendedPropertyTypes<T, P>

/**
 * Safely retrieves the value of a deeply nested property from an object,
 * using a dot-separated path string.
 *
 * - Supports arrays with numeric indices (e.g. `"posts.0.title"`)
 * - Provides **autocomplete** for valid property paths
 * - Infers the return type based on the given path
 *
 * @param obj The object to query
 * @param path The property path (dot-separated string, autocompleted from type `T`)
 *
 * @example
 * const user = {
 *   id: "123",
 *   posts: [{ title: "Hello", tags: ["ts", "types"] }]
 * };
 *
 * const a = getPathValue(user, "id");                 // string
 * const b = getPathValue(user, "posts.0.title");      // string
 * const c = getPathValue(user, "posts.0.tags.0");     // string
 *
 * // Compile-time error: not a valid path
 * const d = getPathValue(user, "posts.tags");
 */
export function getPathValue<T, P extends ExtendedPropertyNames<T>>(obj: T, path: P): PathValue<T, P> {
  const keys = path.split('.')
  let result: unknown = obj
  for (const key of keys) {
    if (result == null) break // safeguard for undefined/null

    // if key is numeric, treat it as array index
    const idx = Number(key)
    result = Number.isNaN(idx)
      ? (result as Record<string, unknown>)[key]
      : (result as unknown[])[idx]
  }
  return result as PathValue<T, P>
}

/**
 * Extracts the element type from an array type `T`.
 * If `T` is not an array, returns `T` itself.
 *
 * @example
 * type A = Unpacked<number[]>; // number
 * type B = Unpacked<string>;   // string
 */
export type Unpacked<T> = T extends (infer U)[] ? U : T
